import { defineMessage } from 'react-intl';

import { NODE_ENV } from '@/build-config';
import { queryClient } from '@/queries/client';
import { queryKeys } from '@/queries/keys';
import KVStore from '@/storage/kv-store';
import { updateMe, getClient, getCurrentAccountId } from '@/stores/auth';
import { useSettingsStore } from '@/stores/settings';
import toast from '@/toast';

import type { Settings } from '@/schemas/frontend-settings';

const FE_NAME = NODE_ENV === 'production' ? 'nicolium' : 'nicolium_dev';

/** Options when changing/saving settings. */
type SettingOpts = {
  /** Whether to display an alert when settings are saved. */
  showAlert?: boolean;
  save?: boolean;
};

const saveSuccessMessage = defineMessage({
  id: 'settings.save.success',
  defaultMessage: 'Preferences saved',
});

const changeSetting = (
  path: string[],
  value: any | ((previousValue: any) => any),
  opts?: SettingOpts,
) => {
  useSettingsStore.getState().actions.changeSetting(path, value);

  if (opts?.save !== false) return saveSettings(opts, path[0] === 'storeSettingsInNotes');
};

const saveSettings = (opts?: SettingOpts, isNotesChange?: boolean) => {
  const currentAccountId = getCurrentAccountId();
  if (typeof currentAccountId !== 'string') return;

  const {
    userSettings,
    actions: { userSettingsSaving },
    userSettingsLoaded,
  } = useSettingsStore.getState();

  if (!userSettingsLoaded) return;
  if (userSettings.saved) return;

  const { saved, ...data } = userSettings;

  updateSettingsStore(data, isNotesChange)
    .then(() => {
      userSettingsSaving();
      if (opts?.showAlert) {
        toast.success(saveSuccessMessage);
      }
    })
    .catch((error) => {
      toast.showAlertForError(error);
    });
};

/** Update settings store for Mastodon, etc. */
const updateAuthAccount = async (url: string, settings: any) => {
  const key = `authAccount:${url}`;
  const oldAccount: any = await KVStore.getItem(key);
  try {
    if (!oldAccount) return;
    oldAccount.settings_store ??= {};
    oldAccount.settings_store[FE_NAME] = settings;
    await KVStore.setItem(key, oldAccount);
  } catch (error) {
    console.error(error);
  }
};

const updateSettingsStore = async (settings: Partial<Settings>, isNotesChange?: boolean) => {
  const client = getClient();
  const currentAccountId = getCurrentAccountId();

  if (client.features.frontendConfigurations) {
    return updateMe({
      settings_store: {
        [FE_NAME]: settings,
      },
    });
  } else {
    if (client.features.notes && (settings.storeSettingsInNotes || isNotesChange)) {
      const note = (await client.accounts.getRelationships([currentAccountId as string]))[0]?.note;
      const settingsNote = `<nicolium-config>${encodeURIComponent(JSON.stringify(settings))}</nicolium-config>`;

      let newNote;
      if (settings.storeSettingsInNotes) {
        if (/<nicolium-config>(.*)<\/nicolium-config>/.test(note || '')) {
          newNote = note!.replace(/<nicolium-config>(.*)<\/nicolium-config>/, settingsNote);
        } else {
          newNote = `${note || ''}\n\n${settingsNote}`;
        }
      } else {
        newNote = note ? note.replace(/<nicolium-config>(.*)<\/nicolium-config>/, '') : '';
      }
      client.accounts.updateAccountNote(currentAccountId as string, newNote);
    }

    const accountId = currentAccountId;
    if (typeof accountId !== 'string') return;
    const account = queryClient.getQueryData(queryKeys.accounts.show(accountId));
    if (!account) return;

    return updateAuthAccount(account.url, settings);
  }
};

export { FE_NAME, changeSetting, saveSettings, updateSettingsStore };
